// Sorte de feuille « vérifier chaque affirmation » (goal.kind = 'decide', lot M3) — mécanisme général du noyau,
// sans règle de chapitre : les gestes viennent toujours des packs (« Calculer », règles de réécriture).
//
// Une affirmation = une ou plusieurs relations (kernel.claims[].rels : « 1 + 1 + 1 = 2² », « √10 > 3 »). L'élève
// écrit des CHAÎNES de calculs sur des nombres (« 2 × (2 + 1) ÷ 2 = 2 × 3 ÷ 2 = 3 ») ; chaque maillon doit être
// vrai, et justifié en un geste depuis le maillon d'avant (le juge s'en charge). Une affirmation est TRANCHÉE
// quand, pour chacune de ses relations, un des membres est écrit sur la feuille et que les deux membres ont une
// valeur connue (écrits sous forme de valeur, ou écrits dans une chaîne qui arrive à une valeur).
// Avec `single` (QCM à une bonne réponse) : une affirmation vraie tranchée tranche les autres (fausses) ; toutes
// fausses sauf une, la dernière est vraie (élimination).
//
// Formes (comme le noyau v40) :
//   - égalité + `form` (irreducible, scientific, primes) : vraie si l'égalité est vraie ET que son membre de droite,
//     tel qu'écrit, est dans la forme ; tranchée quand le membre de gauche est écrit, a une valeur, et que sa classe
//     d'égalité contient une écriture dans la forme ;
//   - écriture seule + propriété (`form`, dont « prime » = nombre premier, ou `divisibleBy`), `not` = négation :
//     tranchée d'après ce qui est écrit (un produit d'entiers ≥ 2 égal au nombre → pas premier ; chaque premier
//     jusqu'à √n qui laisse un reste → premier ; « d × q » → divisible par d ; « d × q + r » avec 0 < r < d → non).
//   - écriture seule + DIVISEURS (`divisors` : { of: count | sum | product, value, proper? }, 30/10) : « 12 a 6
//     diviseurs », « la somme des diviseurs de 28 (sauf 28) vaut 28 ». Tranchée quand TOUTES les paires de
//     diviseurs sont écrites (« 1 × 12 = 12 », « 2 × 6 = 12 », « 12 ÷ 3 = 4 ») ; somme et produit : écrits aussi,
//     avec leur valeur (« 1 + 2 + 3 + 4 + 6 + 12 = 28 »). Le nombre de diviseurs se tranche aussi par la formule :
//     la décomposition écrite (« 12 = 2² × 3 ») et le produit des exposants plus 1 calculé (« (2 + 1) × (1 + 1) = 6 »).
//
// Lettres (lot I4) :
//   - valeur proposée (`at` : { x: '3' }) : l'affirmation se lit avec la valeur remplacée (« 4 × 3 − 7 ≥ 5 ») ; une
//     ligne « 4x − 7 = 4 × 3 − 7 = … » remplace d'abord (« Remplacer »), puis calcule sur des nombres ;
//   - identité (égalité en lettres, sans `at`) : vraie si les deux membres ont les mêmes valeurs pour toutes les
//     valeurs des lettres ; tranchée vraie quand les deux membres sont reliés par des lignes, fausse quand chacun est
//     relié à une écriture développée et réduite et que ces deux écritures diffèrent.

import { isLeaf, symbols, gcd, num } from './ast.js';
import { light, keyOf, features } from './rewrite.js';
import { evaluate, isRelation, toF } from './equiv.js';

export const CLAIM_FORMS = ['irreducible', 'scientific', 'primes', 'prime'];
const EQ_FORMS = ['irreducible', 'scientific', 'primes'];

/** Affirmations de la feuille (relations lues), ou null si la feuille n'est pas une feuille d'affirmations. */
export function readClaims(kernel, table, tryParse) {
  if (kernel?.goal?.kind !== 'decide' || !Array.isArray(kernel.claims) || !kernel.claims.length)
    return null;
  return kernel.claims.map((c, index) => readClaim(c, index, table, tryParse));
}

function readClaim(c, index, table, tryParse, expr0 = null) {
  const rels = expr0 ? [expr0] : (c.rels ?? []).map((t) => tryParse(String(t)));
  const form = c.form ?? null;
  const divisibleBy = c.divisibleBy != null ? Number(c.divisibleBy) : null;
  const divisors = c.divisors != null ? readDivisors(c.divisors) : null;
  const not = !!c.not;
  const off = { index, ok: false, rels, parts: [], keys: [], truth: null };
  if (!rels.length || rels.some((r) => !r)) return off;
  if (c.divisors != null && !divisors) return off;
  if ([form, divisibleBy, divisors].filter(Boolean).length > 1) return off;
  if (c.at !== undefined) {
    // valeur proposée : l'affirmation se lit avec les valeurs remplacées
    if (!c.at || typeof c.at !== 'object') return off;
    const env = {};
    for (const [v, t] of Object.entries(c.at)) {
      const a = tryParse(String(t));
      if (!a || !isNumeric(a, table)) return off;
      env[v] = a;
    }
    // écriture seule + propriété (« n² + n + 41 est premier » pour n = 40) : la propriété du nombre obtenu
    if (rels.length === 1 && !isRelation(rels[0], table)) {
      const sub = replaceSyms(rels[0], env);
      if (!isNumeric(sub, table)) return off;
      const r = readClaim({ ...c, at: undefined, rels: [] }, index, table, () => sub, sub);
      if (!r.ok || !r.prop) return off;
      return { ...r, at: env, letterKeys: [[keyOf(light(rels[0], table))]] };
    }
    if (form || divisibleBy || divisors || not) return off;
    if (!rels.every((r) => isRelation(r, table))) return off;
    const subst = rels.map((r) => replaceSyms(r, env));
    if (subst.some((r) => !isNumeric(r, table))) return off;
    const parts = subst.map((r) => r.k.map((m) => light(m, table)));
    return {
      index,
      ok: true,
      rels: subst,
      at: env,
      letterKeys: rels.map((r) => r.k.map((m) => keyOf(light(m, table)))),
      parts,
      keys: parts.map((ms) => ms.map(keyOf)),
      truth: subst.every((r) => relHolds(r, table)),
    };
  }
  if (rels.some((r) => !isNumeric(r, table))) {
    // identité en lettres : une égalité, rien d'autre
    const r = rels[0];
    if (
      rels.length !== 1 ||
      form ||
      divisibleBy ||
      divisors ||
      not ||
      table.nodes[r.t]?.relation !== 'eq' ||
      r.k.length !== 2
    )
      return off;
    const parts = [r.k.map((m) => light(m, table))];
    return {
      index,
      ok: true,
      identity: true,
      rels,
      parts,
      keys: parts.map((ms) => ms.map(keyOf)),
      truth: fnEqual(r.k[0], r.k[1], table),
    };
  }
  const prop = rels.length === 1 && !isRelation(rels[0], table);
  if (prop) {
    // écriture seule + propriété
    if (!form && !divisibleBy && !divisors) return off;
    if (form && !CLAIM_FORMS.includes(form)) return off;
    const expr = rels[0];
    let base;
    if (divisors) {
      const n = intValue(expr, table);
      if (n === null || n < 1 || n > DIVISORS_MAX) return off;
      base = divisorStat(n, divisors) === divisors.value;
    } else if (form === 'prime' || divisibleBy) {
      const n = intValue(expr, table);
      if (n === null) return off;
      base = form === 'prime' ? isPrime(n) : divisibleBy >= 2 && n % divisibleBy === 0;
    } else
      base =
        inForm(expr, form, table) || (form === 'irreducible' && factoredIrreducible(expr, table));
    const L = light(expr, table);
    return {
      index,
      ok: true,
      rels,
      prop: true,
      form,
      divisibleBy,
      divisors,
      not,
      expr,
      n: intValue(expr, table),
      parts: [[L]],
      keys: [[keyOf(L)]],
      truth: not ? !base : base,
    };
  }
  if (!rels.every((r) => isRelation(r, table))) return off;
  if (divisibleBy || divisors || not || form === 'prime') return off;
  if (form) {
    if (!EQ_FORMS.includes(form)) return off;
    const r = rels[0];
    if (rels.length !== 1 || table.nodes[r.t]?.relation !== 'eq' || r.k.length !== 2) return off;
  }
  const parts = rels.map((r) => r.k.map((m) => light(m, table)));
  const holds = rels.every((r) => relHolds(r, table));
  return {
    index,
    ok: true,
    rels,
    form,
    parts,
    keys: parts.map((ms) => ms.map(keyOf)),
    truth: holds && (!form || inForm(rels[0].k[1], form, table)),
  };
}

/** Remplace des lettres par des écritures (env : nom → AST). */
export function replaceSyms(n, env) {
  if (n.t === 'Sym' && Object.prototype.hasOwnProperty.call(env, n.name)) return env[n.name];
  if (isLeaf(n)) return n;
  return { ...n, k: n.k.map((c) => replaceSyms(c, env)) };
}

// valeurs d'essai des lettres d'une identité
const PROBES = [
  [37, 100],
  [113, 100],
  [-271, 100],
  [33, 10],
  [5, 7],
  [-13, 6],
  [27, 4],
];
/** Deux écritures en lettres ont-elles les mêmes valeurs (aux points d'essai) ? */
export function fnEqual(a, b, table) {
  const vars = [...new Set([...symbols(a), ...symbols(b)])].filter(
    (v) => !table.syntax.constants?.[v],
  );
  let compared = 0;
  for (let i = 0; i < PROBES.length; i++) {
    const env = {};
    vars.forEach((v, j) => {
      const [p, q] = PROBES[(i + 3 * j) % PROBES.length];
      env[v] = num(p, q);
    });
    const x = toNum(evaluate(a, env, table)),
      y = toNum(evaluate(b, env, table));
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    if (!sameValue(x, y)) return false;
    compared++;
  }
  return compared >= 3;
}
const toNum = (v) => (v === undefined || v === null ? NaN : typeof v === 'number' ? v : toF(v));

/** Écriture développée et réduite en lettres : plus rien à développer, calculer ni réunir. */
export function isLetterValue(L, table) {
  const f = features(L, table);
  return !f.foldable && !f.collectable && !f.nonExpanded;
}

/** Expression sur des nombres seulement (constantes comprises : π, e). */
export function isNumeric(n, table) {
  return [...symbols(n)].every((s) => table.syntax.constants?.[s]);
}

/** Valeur d'une expression numérique (nombre JS), ou NaN. */
export function numValue(n, table) {
  const v = evaluate(n, {}, table);
  if (v === undefined || v === null) return NaN;
  const x = typeof v === 'number' ? v : toF(v);
  return Number.isFinite(x) ? x : NaN;
}

export function sameValue(a, b) {
  return (
    Number.isFinite(a) &&
    Number.isFinite(b) &&
    Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a), Math.abs(b))
  );
}

// ── Valeur interdite : division par 0 ───────────────────────────────────
/**
 * Une écriture numérique qui divise par 0 (« (2 + 1) ÷ (2 − 2) », « 3 ÷ 0 ») : la valeur de son numérateur (le
 * nombre « divisé par 0 »), null sinon. Deux écritures interdites sont reliées par un calcul quand leurs
 * numérateurs ont la même valeur.
 */
export function divByZero(n, table) {
  if (isLeaf(n) || evalOf(n, table) !== 'div' || n.k.length !== 2 || !isNumeric(n, table))
    return null;
  const d = numValue(n.k[1], table);
  if (d !== 0) return null;
  const a = numValue(n.k[0], table);
  return Number.isFinite(a) ? a : null;
}

/** « 3 ÷ 0 » écrit tel quel : la valeur interdite, plus rien à calculer. */
export function isUndefLiteral(n, table) {
  return divByZero(n, table) !== null && isLeaf(n.k[0]) && isLeaf(n.k[1]);
}

/** Une relation (chaîne comprise) entre nombres est-elle vraie ? */
export function relHolds(rel, table) {
  const v = evaluate(rel, {}, table);
  if (typeof v === 'boolean') return v;
  const vals = rel.k.map((m) => numValue(m, table));
  return vals.every((x) => sameValue(x, vals[0]));
}

/** Écriture finale d'une valeur : plus rien à calculer (« 3 », « 2/3 », « −5 », « √2 », « π »). */
export function isValueForm(L, table) {
  if (!isNumeric(L, table) || !Number.isFinite(numValue(L, table))) return false;
  if (isLeaf(L)) return true;
  if (!simplest(L, table)) return false;
  const f = features(L, table);
  return !f.foldable && !f.collectable;
}

// Une valeur s'écrit simplement : pas de fraction réductible (12 ÷ 15), pas de racine qui se simplifie (√8 = 2√2).
function simplest(n, table) {
  if (isLeaf(n)) return true;
  const ev = evalOf(n, table);
  if (ev === 'div' && n.k.length === 2 && isInt(n.k[0]) && isInt(n.k[1])) {
    if (gcd(Math.abs(n.k[0].p), Math.abs(n.k[1].p)) !== 1 || Math.abs(n.k[1].p) === 1) return false;
  }
  if (ev === 'sqrt' && isInt(n.k[0])) {
    const v = n.k[0].p;
    for (let d = 2; d * d <= v; d++) if (v % (d * d) === 0) return false;
  }
  return n.k.every((c) => simplest(c, table));
}

// ── Formes et nombres entiers ─────────────────────────────────────────────

const evalOf = (n, table) => (isLeaf(n) ? null : table.nodes[n.t]?.eval);
const isInt = (n) => n?.t === 'Num' && n.q === 1;
const intOf = (n, table) => {
  if (isInt(n)) return n.p;
  if (evalOf(n, table) === 'neg' && isInt(n.k[0])) return -n.k[0].p;
  return null;
};

// facteurs d'un produit tel qu'écrit (l'arbre brut garde les produits imbriqués deux à deux)
const factorsOf = (n, table) =>
  evalOf(n, table) === 'mul' ? n.k.flatMap((c) => factorsOf(c, table)) : [n];

function intValue(n, table) {
  const v = evaluate(n, {}, table);
  if (v && typeof v === 'object' && v.q !== undefined && Number(v.q) === 1) return Number(v.p);
  if (typeof v === 'number' && Number.isInteger(v)) return v;
  return null;
}

export function isPrime(n) {
  if (!Number.isInteger(n) || n < 2) return false;
  for (let d = 2; d * d <= n; d++) if (n % d === 0) return false;
  return true;
}

/** L'écriture (telle qu'écrite, non normalisée) est-elle dans la forme demandée ? */
export function inForm(raw, form, table) {
  const ev = evalOf(raw, table);
  if (form === 'irreducible') {
    if (intOf(raw, table) !== null) return true;
    const body = ev === 'neg' ? raw.k[0] : raw;
    if (evalOf(body, table) !== 'div') return false;
    const [a, b] = body.k.map((c) => intOf(c, table));
    return a !== null && b !== null && b > 1 && gcd(Math.abs(a), b) === 1;
  }
  if (form === 'scientific') {
    const body = ev === 'neg' ? raw.k[0] : raw;
    if (isPow10(body, table)) return true;
    const fs = factorsOf(body, table);
    if (fs.length !== 2) return false;
    const [a, p] = fs;
    if (a.t !== 'Num' || !isPow10(p, table)) return false;
    const x = Math.abs(Number(a.p) / Number(a.q));
    return x >= 1 && x < 10;
  }
  if (form === 'primes' || form === 'prime') {
    return factorsOf(raw, table).every((f) => {
      if (isInt(f)) return isPrime(f.p);
      if (evalOf(f, table) === 'pow')
        return isInt(f.k[0]) && isPrime(f.k[0].p) && isInt(f.k[1]) && f.k[1].p >= 2;
      return false;
    });
  }
  return false;
}

/**
 * « 2² ÷ (3 × 5) » : une fraction dont le numérateur ou le dénominateur est écrit en produit (ou en puissance) d'entiers
 * est irréductible quand leurs VALEURS n'ont aucun facteur commun (même règle que l'ancien moteur, lot 6.57).
 */
function factoredIrreducible(raw, table) {
  if (evalOf(raw, table) !== 'div') return false;
  const side = (n) =>
    factorsOf(n, table).every(
      (f) => isInt(f) || (evalOf(f, table) === 'pow' && isInt(f.k[0]) && isInt(f.k[1])),
    )
      ? intValue(n, table)
      : null;
  const [p, q] = raw.k.map(side);
  return p !== null && q !== null && q > 1 && gcd(Math.abs(p), q) === 1;
}

function isPow10(n, table) {
  return (
    evalOf(n, table) === 'pow' && isInt(n.k[0]) && n.k[0].p === 10 && intOf(n.k[1], table) !== null
  );
}

// ── Ce que montre la feuille ──────────────────────────────────────────────

/**
 * Classes d'égalité des lignes gardées : membres écrits (clés), valeur connue de chaque classe (un membre écrit sous
 * forme de valeur), écritures brutes de chaque classe (pour les formes). Les lignes de départ et données ne
 * comptent pas : ce sont les affirmations elles-mêmes.
 */
export function sheetClasses(lines, table) {
  const written = new Set(),
    eqChains = [];
  const parent = new Map();
  const find = (k) => {
    if (!parent.has(k)) return k;
    while (parent.get(k) !== k) k = parent.get(k);
    return k;
  };
  const add = (k) => {
    if (!parent.has(k)) parent.set(k, k);
  };
  for (const line of lines) {
    if (
      !line.ast ||
      !isRelation(line.ast, table) ||
      line.status === 'start' ||
      line.status === 'given'
    )
      continue;
    const ms = line.ast.k.map((m) => ({ raw: m, L: light(m, table) }));
    for (const m of ms) {
      m.key = keyOf(m.L);
      written.add(m.key);
      add(m.key);
    }
    if (table.nodes[line.ast.t]?.relation === 'eq') {
      eqChains.push(ms);
      for (let i = 1; i < ms.length; i++) parent.set(find(ms[i].key), find(ms[0].key));
    }
  }
  const value = new Map(),
    members = new Map();
  for (const ms of eqChains)
    for (const m of ms) {
      const r = find(m.key);
      if (!members.has(r)) members.set(r, []);
      members.get(r).push(m.raw);
      if (isValueForm(m.L, table)) value.set(r, numValue(m.L, table));
    }
  return {
    written,
    find,
    value,
    members: (k) => members.get(find(k)) ?? [],
    // chaque membre écrit dans une égalité (écriture brute, clé) : sommes et produits des diviseurs
    entries: eqChains.flat(),
  };
}

/** Clés écrites et valeurs connues (clé → valeur). */
export function knownValues(lines, table) {
  const c = sheetClasses(lines, table),
    known = new Map();
  for (const k of c.written) if (c.value.has(c.find(k))) known.set(k, c.value.get(c.find(k)));
  return { written: c.written, known };
}

function decided(claim, C, table) {
  // une valeur interdite écrite (« 3 ÷ 0 ») tranche aussi : on ne divise jamais par 0
  const knownOf = (L, k) =>
    C.value.has(C.find(k)) ||
    isValueForm(L, table) ||
    isUndefLiteral(L, table) ||
    (C.written.has(k) && C.members(k).some((m) => isUndefLiteral(m, table)));
  if (claim.identity) {
    const [ka, kb] = claim.keys[0];
    const [La, Lb] = claim.parts[0];
    if (!C.written.has(ka) && !C.written.has(kb)) return false;
    if (C.written.has(ka) && C.written.has(kb) && C.find(ka) === C.find(kb)) return true;
    // une écriture développée et réduite de chaque membre, et elles diffèrent
    const valueOf = (L, k) => {
      if (isLetterValue(L, table)) return keyOf(L);
      const m = C.members(k)
        .map((raw) => light(raw, table))
        .find((x) => isLetterValue(x, table));
      return m ? keyOf(m) : null;
    };
    const va = valueOf(La, ka),
      vb = valueOf(Lb, kb);
    return va !== null && vb !== null && va !== vb;
  }
  if (claim.prop) {
    const k = claim.keys[0][0];
    // diviseurs : le nombre n'a pas à être écrit seul (« 12 ÷ 3 = 4 » montre une paire)
    if (claim.divisors) return divisorsDecided(claim, C, table);
    if (!C.written.has(k)) return false;
    const ms = C.members(k);
    if (claim.form === 'prime') return claim.n !== null && primeEvidence(claim.n, ms, table);
    if (claim.divisibleBy) return divEvidence(claim.divisibleBy, ms, table);
    return ms.some((m) => inForm(m, claim.form, table));
  }
  if (claim.form) {
    const [lk] = claim.keys[0];
    // une écriture dans la forme (« 1,2 × 10⁴ ») donne aussi la valeur
    return C.written.has(lk) && C.members(lk).some((m) => inForm(m, claim.form, table));
  }
  const relDecided = (ms, r) =>
    (ms.some((_, i) => C.written.has(claim.keys[r][i])) &&
      ms.every((m, i) => knownOf(m, claim.keys[r][i]))) ||
    // égalité dont les deux membres sont reliés par des lignes justes (« 10⁷ ÷ 10⁴ = 10³ » écrite)
    (table.nodes[claim.rels[r].t]?.relation === 'eq' &&
      ms.length === 2 &&
      claim.keys[r].every((k) => C.written.has(k)) &&
      C.find(claim.keys[r][0]) === C.find(claim.keys[r][1]));
  // plusieurs relations (« 3⁷ > 1000 et 3⁶ ≤ 1000 ») : une seule relation tranchée fausse suffit
  if (claim.truth === false && claim.parts.length > 1)
    return claim.parts.some((ms, r) => relDecided(ms, r) && !relHolds(claim.rels[r], table));
  return claim.parts.every(relDecided);
}

// « d × q + r » écrit (entiers, 0 < r < d) → [d, r]
function remainders(ms, table) {
  const out = [];
  for (const m of ms) {
    if (evalOf(m, table) !== 'add' || m.k.length !== 2) continue;
    const [a, b] = m.k;
    const pr = evalOf(a, table) === 'mul' ? [a, b] : evalOf(b, table) === 'mul' ? [b, a] : null;
    if (!pr) continue;
    const r = intOf(pr[1], table);
    const fs = factorsOf(pr[0], table).map((c) => intOf(c, table));
    if (fs.length !== 2) continue;
    if (r === null || fs.some((f) => f === null)) continue;
    for (const d of fs) if (r > 0 && r < d) out.push([d, r]);
  }
  return out;
}
const products = (ms, table) =>
  ms
    .filter((m) => evalOf(m, table) === 'mul')
    .map((m) => factorsOf(m, table).map((c) => intOf(c, table)))
    .filter((fs) => fs.every((f) => f !== null));

function primeEvidence(n, ms, table) {
  if (products(ms, table).some((fs) => fs.every((f) => f >= 2))) return true; // pas premier
  const tested = new Set(remainders(ms, table).map(([d]) => d));
  for (let p = 2; p * p <= n; p++) if (isPrime(p) && !tested.has(p)) return false;
  return n >= 2;
}

function divEvidence(d, ms, table) {
  if (products(ms, table).some((fs) => fs.includes(d))) return true;
  return remainders(ms, table).some(([q]) => q === d);
}

// ── Diviseurs d'un entier (30/10) ─────────────────────────────────────────
// « Combien 12 a-t-il de diviseurs ? » : l'élève écrit les paires (« 1 × 12 = 12 », « 2 × 6 = 12 », « 3 × 4 = 12 ») ;
// quand elles y sont toutes, le nombre de diviseurs (6), leur somme ou leur produit se lit.

/** plus grand entier dont la feuille liste les diviseurs */
const DIVISORS_MAX = 1_000_000;
const DIVISORS_OF = ['count', 'sum', 'product'];

function readDivisors(d) {
  if (!d || typeof d !== 'object' || !DIVISORS_OF.includes(d.of)) return null;
  const v = String(d.value ?? '');
  if (!/^\d{1,30}$/.test(v)) return null;
  return { of: d.of, value: BigInt(v), proper: d.proper === true };
}

/** Diviseurs positifs de n (entier ≥ 1), croissants ; `proper` : sans n lui-même. */
export function divisorsOf(n, proper = false) {
  const small = [],
    big = [];
  for (let a = 1; a * a <= n; a++)
    if (n % a === 0) {
      small.push(a);
      if (a * a !== n) big.unshift(n / a);
    }
  const all = [...small, ...big];
  return proper ? all.filter((d) => d !== n) : all;
}

/** les petits diviseurs (a ≤ √n) : un par paire « a × b = n » */
const smallDivisors = (n) => divisorsOf(n).filter((a) => a * a <= n);

function divisorStat(n, spec) {
  const D = divisorsOf(n, spec.proper);
  if (spec.of === 'count') return BigInt(D.length);
  if (spec.of === 'sum') return D.reduce((s, d) => s + BigInt(d), 0n);
  return D.reduce((s, d) => s * BigInt(d), 1n);
}

/**
 * Les paires de diviseurs de n montrées par la feuille (classes d'égalité `C`), par leur petit facteur : « a × b »
 * écrit avec a × b = n, ou « n ÷ a » relié à l'entier n ÷ a (« 12 ÷ 3 = 4 »).
 */
export function divisorPairs(n, C, table) {
  const found = new Set();
  for (const { raw, key } of C.entries) {
    const ev = evalOf(raw, table);
    if (ev === 'mul') {
      const fs = factorsOf(raw, table).map((f) => intOf(f, table));
      if (fs.length === 2 && fs.every((f) => f !== null && f > 0) && fs[0] * fs[1] === n)
        found.add(Math.min(fs[0], fs[1]));
    } else if (ev === 'div' && raw.k.length === 2) {
      const [p, q] = raw.k.map((f) => intOf(f, table));
      if (p === n && q !== null && q > 0 && n % q === 0 && C.value.get(C.find(key)) === n / q)
        found.add(Math.min(q, n / q));
    }
  }
  return found;
}

const pairsComplete = (n, C, table) => {
  const found = divisorPairs(n, C, table);
  return smallDivisors(n).every((a) => found.has(a));
};

const termsOf = (n, table) =>
  evalOf(n, table) === 'add' ? n.k.flatMap((c) => termsOf(c, table)) : [n];
const sorted = (xs) => [...xs].sort((a, b) => a - b);
const sameMultiset = (xs, ys) => {
  if (xs.length !== ys.length) return false;
  const a = sorted(xs),
    b = sorted(ys);
  return a.every((x, i) => x === b[i]);
};
// facteurs d'un produit, les puissances entières développées (« 12³ » → 12, 12, 12)
const expandedFactors = (n, table) =>
  factorsOf(n, table).flatMap((f) => {
    if (evalOf(f, table) !== 'pow') return [intOf(f, table)];
    const b = intOf(f.k[0], table),
      e = intOf(f.k[1], table);
    return b !== null && e !== null && e >= 1 && e <= 30 ? Array(e).fill(b) : [null];
  });

/** La somme ou le produit des diviseurs écrit, avec sa valeur (« 1 + 2 + 3 + 4 + 6 + 12 = 28 », « 12³ = 1728 »). */
function statWritten(n, spec, C, table) {
  const D = divisorsOf(n, spec.proper);
  // produit regroupé par paires : chaque paire fait n (et √n reste seul si n est un carré)
  const half = Math.floor(D.length / 2),
    root = Math.round(Math.sqrt(n));
  const grouped = spec.proper
    ? null
    : [...Array(half).fill(n), ...(root * root === n ? [root] : [])];
  for (const { raw, key } of C.entries) {
    if (!C.value.has(C.find(key))) continue;
    if (spec.of === 'sum') {
      if (evalOf(raw, table) !== 'add') continue;
      const ts = termsOf(raw, table).map((t) => intOf(t, table));
      if (ts.every((t) => t !== null) && sameMultiset(ts, D)) return true;
      continue;
    }
    const ev = evalOf(raw, table);
    if (ev !== 'mul' && ev !== 'pow') continue;
    const fs = expandedFactors(raw, table);
    if (fs.some((f) => f === null)) continue;
    if (sameMultiset(fs, D) || (grouped && sameMultiset(fs, grouped))) return true;
  }
  return false;
}

function primeExponents(n) {
  const out = [];
  for (let p = 2; p * p <= n; p++) {
    let e = 0;
    while (n % p === 0) {
      n /= p;
      e++;
    }
    if (e) out.push(e);
  }
  if (n > 1) out.push(1);
  return out;
}

/**
 * Le nombre de diviseurs par la formule : la décomposition en facteurs premiers de n écrite (« 12 = 2² × 3 »), et le
 * produit des exposants plus 1 calculé (« (2 + 1) × (1 + 1) = 6 »).
 */
function countByFormula(n, nKey, C, table) {
  if (!C.members(nKey).some((m) => !isLeaf(m) && inForm(m, 'primes', table))) return false;
  const want = primeExponents(n).map((e) => e + 1);
  for (const { raw, key } of C.entries) {
    if (!C.value.has(C.find(key))) continue;
    const fs = factorsOf(raw, table);
    if (fs.length !== want.length) continue;
    // un seul facteur premier : « 3 + 1 » écrit (un nombre seul ne montre rien)
    if (want.length === 1 && evalOf(raw, table) !== 'add') continue;
    const vs = fs.map((f) => intValue(f, table));
    if (vs.every((v) => v !== null) && sameMultiset(vs, want)) return true;
  }
  return false;
}

function divisorsDecided(claim, C, table) {
  const n = claim.n,
    spec = claim.divisors;
  if (n === null || n < 1) return false;
  if (spec.of === 'count')
    return pairsComplete(n, C, table) || countByFormula(n, claim.keys[0][0], C, table);
  return pairsComplete(n, C, table) && statWritten(n, spec, C, table);
}

/** Les entiers dont la feuille cherche les diviseurs. */
export function divisorNumbers(claims) {
  return [...new Set((claims ?? []).filter((c) => c.ok && c.divisors).map((c) => c.n))];
}

/** Les paires de diviseurs montrées par des lignes (« n:a », a le petit facteur), pour chaque entier de `ns`. */
export function divisorPairKeys(ns, lines, table) {
  if (!ns.length) return new Set();
  const C = sheetClasses(lines, table);
  const out = new Set();
  for (const n of ns) for (const a of divisorPairs(n, C, table)) out.add(`${n}:${a}`);
  return out;
}

/**
 * Décision de chaque affirmation : true (vraie), false (fausse) ou null (pas encore tranchée), d'après les lignes.
 */
export function claimDecisions(claims, lines, table, single = false) {
  const C = sheetClasses(lines, table);
  const out = claims.map((c) => (c.ok && decided(c, C, table) ? c.truth : null));
  if (single) {
    if (out.some((d) => d === true)) return out.map((d) => d === true);
    const open = out.map((d, i) => (d === null ? i : -1)).filter((i) => i >= 0);
    if (open.length === 1 && claims[open[0]].ok) out[open[0]] = true;
  }
  return out;
}

/** Les membres de toutes les affirmations (clés), pour savoir si une ligne parle de la question. */
export function claimMemberKeys(claims) {
  const s = new Set();
  for (const c of claims) for (const ks of c.keys) for (const k of ks) s.add(k);
  return s;
}

/**
 * Ligne « 4x − 7 = 4 × 3 − 7 = … » d'une affirmation à valeur proposée : l'affirmation dont la valeur remplacée donne le
 * membre suivant, et la même chaîne avec le premier membre remplacé. null sinon.
 */
export function atInstance(claims, ast, table) {
  if (!isRelation(ast, table) || ast.k.length < 2) return null;
  const k0 = keyOf(light(ast.k[0], table));
  const next = ast.k[1];
  if (!isNumeric(next, table)) return null;
  const v = numValue(next, table);
  const vz = divByZero(next, table);
  for (const c of claims) {
    if (!c.ok || !c.at || !c.letterKeys.some((ks) => ks.includes(k0))) continue;
    const sub = replaceSyms(ast.k[0], c.at);
    if (!isNumeric(sub, table)) continue;
    // valeur interdite (« (x + 1) ÷ (x − 2) = (2 + 1) ÷ (2 − 2) ») : même numérateur divisé par 0
    const sz = divByZero(sub, table);
    if (vz !== null || sz !== null) {
      if (vz === null || sz === null || !sameValue(vz, sz)) continue;
    } else if (!sameValue(numValue(sub, table), v)) continue;
    return { claim: c, ast: { ...ast, k: [sub, ...ast.k.slice(1)] } };
  }
  return null;
}

/** La ligne parle-t-elle d'un membre d'une affirmation à valeur proposée (« 4x − 7 ») ? */
export function speaksOfAt(claims, ast, table) {
  if (!isRelation(ast, table)) return false;
  const k0 = keyOf(light(ast.k[0], table));
  return claims.some((c) => c.ok && c.at && c.letterKeys.some((ks) => ks.includes(k0)));
}
